import React, { useMemo } from 'react';
import { AgendaItem } from '@/types';
import { buildMonthCells, sameDay } from '@/lib/dateUtils';
import { EventPill } from './EventPill';

interface Props {
  cursor: Date;
  byDay: Record<string, AgendaItem[]>;
  onDayClick: (d: Date) => void;
}

const DOW_LABELS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];

export function MonthView({ cursor, byDay, onDayClick }: Props) {
  const cells = useMemo(() => buildMonthCells(cursor), [cursor]);
  const today = new Date();

  return (
    <div>
      {/* Cabeçalho DOW */}
      <div className="grid grid-cols-7 border-b border-line dark:border-line-dark">
        {DOW_LABELS.map((d, i) => (
          <div
            key={i}
            className={[
              'px-3.5 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em]',
              i === 0 || i === 6
                ? 'text-ink-placeholder/70 dark:text-ink-placeholder-dark/70'
                : 'text-ink-subtitle dark:text-ink-subtitle-dark',
            ].join(' ')}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grade */}
      <div className="grid grid-cols-7 [grid-auto-rows:minmax(112px,1fr)]">
        {cells.map((c, i) => {
          const dow = i % 7;
          const isWkd = dow === 0 || dow === 6;
          const isToday = !c.out && sameDay(c.date, today);
          const evs = c.out ? [] : (byDay[c.date.toDateString()] ?? []);
          const visible = evs.slice(0, 3);
          const more = evs.length - visible.length;
          const lastOfRow = (i + 1) % 7 === 0;

          const classes = [
            'relative p-2 pb-1.5 border-b border-line dark:border-line-dark transition-colors duration-[180ms]',
            lastOfRow ? '' : 'border-r border-line dark:border-line-dark',
          ];
          if (isToday) {
            classes.push('bg-ink-title dark:bg-brand-500 cursor-pointer');
          } else {
            classes.push('hover:bg-surface-alt dark:hover:bg-surface-dark-alt cursor-pointer');
          }
          if (c.out) classes.push('opacity-40');
          if (isWkd && !isToday) classes.push('opacity-[0.65]');

          return (
            <button
              key={i}
              type="button"
              disabled={c.out}
              onClick={() => !c.out && onDayClick(c.date)}
              className={classes.join(' ') + ' text-left'}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={[
                    isToday
                      ? 'text-[14px] font-semibold text-white'
                      : c.out
                        ? 'text-xs tabular-nums font-medium text-ink-placeholder dark:text-ink-placeholder-dark'
                        : 'text-xs tabular-nums font-medium text-ink-label dark:text-ink-label-dark',
                  ].join(' ')}
                >
                  {String(c.date.getDate()).padStart(2, '0')}
                </span>
                {evs.length > 0 && (
                  <span
                    className={[
                      'text-[10.5px] tabular-nums font-medium',
                      isToday ? 'text-white/65' : 'text-ink-subtitle dark:text-ink-subtitle-dark',
                    ].join(' ')}
                  >
                    {evs.length}
                  </span>
                )}
              </div>

              {visible.map((e, j) => (
                <EventPill key={e.id} event={e} delay={j * 30} inverted={isToday} />
              ))}

              {more > 0 && (
                <div
                  className={[
                    'text-[10.5px] mt-[3px] pl-3',
                    isToday ? 'text-white/65' : 'text-ink-subtitle dark:text-ink-subtitle-dark',
                  ].join(' ')}
                >
                  +{more} mais
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
